/**
 * Obfuscation utility to protect sensitive data in client-side code
 * Note: This is obfuscation, not encryption. It provides protection against casual inspection.
 */

// Simple XOR cipher with key rotation
const OBFUSCATION_KEY = 'W34pp3-Wh1t3L@b3l-S3cur3-K3y';

/**
 * Obfuscate a string value
 */
export const obfuscate = (value: string): string => {
  if (!value) return '';
  
  let result = '';
  const keyLen = OBFUSCATION_KEY.length;
  
  for (let i = 0; i < value.length; i++) {
    const charCode = value.charCodeAt(i);
    const keyChar = OBFUSCATION_KEY.charCodeAt(i % keyLen);
    const obfuscated = charCode ^ keyChar;
    result += String.fromCharCode(obfuscated);
  }
  
  // Convert to base64 to make it look like random data
  return btoa(result);
};

/**
 * Deobfuscate a string value
 */
export const deobfuscate = (obfuscated: string): string => {
  if (!obfuscated) return '';
  
  try {
    // Decode from base64
    const decoded = atob(obfuscated);
    
    let result = '';
    const keyLen = OBFUSCATION_KEY.length;
    
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i);
      const keyChar = OBFUSCATION_KEY.charCodeAt(i % keyLen);
      const original = charCode ^ keyChar;
      result += String.fromCharCode(original);
    }
    
    return result;
  } catch (error) {
    console.error('Error deobfuscating value:', error);
    return '';
  }
};

/**
 * Get obfuscated environment variable
 */
export const getObfuscatedEnv = (key: string): string => {
  const value = import.meta.env[key];
  return value ? obfuscate(value) : '';
};

/**
 * Get Account ID from environment (raw value, not obfuscated)
 * The obfuscation happens during build, this just accesses it in a less obvious way
 */
export const getAccountId = (): string | null => {
  // Split the env access to make it harder to grep in bundled code
  const parts = ['VITE', 'ACCOUNT', 'ID'];
  const envKey = parts.join('_');
  const rawValue = (import.meta.env as any)[envKey];
  
  return rawValue || null;
};

/**
 * Get Encryption Key from environment (raw value, not obfuscated)
 * The obfuscation happens during build, this just accesses it in a less obvious way
 */
export const getEncryptKey = (): string | null => {
  // Split the env access to make it harder to grep in bundled code
  const parts = ['VITE', 'ENCRYPT', 'KEY'];
  const envKey = parts.join('_');
  const rawValue = (import.meta.env as any)[envKey];
  
  return rawValue || null;
};

/**
 * Get Google Analytics ID from environment
 * Note: Analytics IDs are public by nature (visible in network requests)
 */
export const getGAMeasurementId = (): string | null => {
  const parts = ['VITE', 'GA', 'MEASUREMENT', 'ID'];
  const envKey = parts.join('_');
  const rawValue = (import.meta.env as any)[envKey];
  
  return rawValue || null;
};

/**
 * Get Meta Pixel ID from environment
 * Note: Analytics IDs are public by nature (visible in network requests)
 */
export const getMetaPixelId = (): string | null => {
  const parts = ['VITE', 'META', 'PIXEL', 'ID'];
  const envKey = parts.join('_');
  const rawValue = (import.meta.env as any)[envKey];
  
  return rawValue || null;
};

/**
 * Get Google Tag Manager ID from environment
 * Note: Analytics IDs are public by nature (visible in network requests)
 */
export const getGtmId = (): string | null => {
  const parts = ['VITE', 'GTM', 'ID'];
  const envKey = parts.join('_');
  const rawValue = (import.meta.env as any)[envKey];

  return rawValue || null;
};

